import { hitRunConfirm, isIssueRow, urgentTag } from './urgent.js';
import { notACarrierName } from './side.js';
import { controllerReason } from './reasons.js';
const NEEDS_PERSON_TEXT = controllerReason('DEFER', 'APPROVED_NEEDS_PERSON').text;
/** Each group: the action in words (the group header), and its place on the page (most decisive first). */
export const TODO_GROUPS = {
    // Kimi's UI assessment (Rod 2026-10-13 "1 a", change 5): a client waiting on a person comes first in every queue
    reply: { label: "Answer the client's reply", order: 0.5, how: 'Read it, answer the client, then mark the conversation resolved in Banjaxed' },
    hitrun: { label: 'Confirm the hit and run (no 3P), then open 1P / UM', order: 1, how: 'The client advocate confirms; then the 1P / UM claim opens' },
    report: { label: 'Read the police report: is our client at fault?', order: 2, how: 'If the report says our client is at fault, say so; otherwise the claim opens' },
    claim: { label: 'Enter the claim number', order: 3, how: 'Type the number the carrier gave; the letter goes out next' },
    policy: { label: 'Enter the policy number', order: 4, how: 'Type it from the card or the dec page; the AI calls within 15 minutes' },
    add: { label: 'Add the insurance already in our files', order: 5, how: 'Add the record the police report, dec page or card names; the AI calls next' },
    // a second client's side the system will not copy (Rod 10-14 #46 #7: these sat under "add", whose "the AI calls next" is never true for them)
    second: { label: "Second client: add it from the main client's case", order: 5.2, how: "Add the record in Salesforce from the main client's case; the AI never calls for a second client" },
    // Kimi change 10 (Rod 2026-10-13 "1a 2a"): a record whose carrier is not an insurer ("Unknown Unknown", "No insurance", "SELF") is a records
    // fix, not calling work: the AI's carrier gate never dials it, so it sat under "enter the policy number" or "call the carrier" forever
    carrier: { label: 'Fix the carrier on the insurance record', order: 5.5, how: 'Put the real insurer on the record; the AI cannot call a placeholder' },
    insurer: { label: "Find the at-fault driver's insurer", order: 6, how: 'Check the police report, the intake and the exchange, then add the DEF record' },
    police: { label: 'Start the police report request', order: 7, how: "Nudge the request's owner, or get the insurer from the client" },
    client: { label: 'Ask the client about their own insurance', order: 8, how: 'Ask the client for their card or their insurer' },
    phone: { label: "Enter the carrier's claims phone", order: 9, how: 'Type the claims line; the AI calls it next' },
    call: { label: 'Call or file with the carrier yourself', order: 10, how: 'Call or file it, then type the claim number' },
    lor: { label: 'Send the letter of representation', order: 11, how: "Send it; the robot needs the adjuster's email or fax" },
    look: { label: 'Look at it', order: 12, how: 'Open the case and decide' },
};
const GAP_GROUP = {
    companion: 'second', companion_own: 'client', other_party_only: 'insurer', police_names: 'add', police_client_fault: 'report', hit_and_run: 'hitrun', hit_and_run_unclear: 'report',
    police_no_insurer: 'insurer', police_closed: 'insurer', police_not_started: 'police', no_report_filed: 'insurer', not_requested_driver: 'police',
    not_requested_no_driver: 'police', not_requested: 'police', plt_police: 'add', plt_dec: 'add', plt_card: 'add', plt_insurer_known: 'add',
    no_own_auto: 'client', plt_minor: 'client', plt_texted_before: 'client', plt_conflict: 'client', plt_unknown: 'client', plt_said_none: 'client',
};
const FIX_GROUP = {
    nopolicy: 'policy', aifail: 'policy', missing: 'call', note: 'claim', placeholder: 'claim', companion: 'claim', companion_carrier: 'claim', tcr: 'add', nocarrier: 'insurer',
    no3p: 'insurer', police: 'police', nophone: 'phone', live: 'call', elsewhere: 'call', cap: 'call', review: 'call', handoff: 'call', nocall: 'call',
};
/** The one thing a person does for this row. */
export function todoGroup(r) {
    if (hitRunConfirm(r))
        return 'hitrun';
    if (r.status === 'need' && r.replies?.some((x) => x.waiting))
        return 'reply';
    // the own-policy row: the client's own insurance for PLT, the at-fault insurer for DEF (Codex 10-14 #46 v4 #5), after hit and run and a
    // waiting reply as every other row (v5 #5)
    if (r.gap?.key === 'companion_own')
        return r.side === 'DEF' ? 'insurer' : 'client';
    if (!r.gap && r.carrier && notACarrierName(r.carrier))
        return 'carrier';
    // the controller's own words on a row it parked (decide.js NEW_INFO_NEEDED_TO_RECALL; the AI's hand-off to our team)
    if (/new info needed to recall/i.test(r.text))
        return 'policy';
    // the approved number did not open a claim (approved_call.js APPROVED_NEEDS_PERSON): the right policy number is what is missing, and the Found
    // group's needs-a-person row takes over this group only (review of #105, N2), so the record is not listed twice for one need
    if (r.text === NEEDS_PERSON_TEXT)
        return 'policy';
    if (/transferred the call to our team/i.test(r.text))
        return 'call';
    if (r.gap)
        return GAP_GROUP[r.gap.key] ?? 'look';
    if (r.status !== 'need' && r.lorAsk)
        return 'lor';
    return (r.fix && FIX_GROUP[r.fix]) || 'look';
}
/** Rows a rule should close on its own, so they are not a person's to-do. Rod + Neila 10-09: when the client already said they have no auto
 *  insurance, the system closes the 1P ask and tells the case manager in Chatter; nobody re-asks. */
export function isParked(r) {
    if (r.gap?.key === 'plt_said_none')
        return true;
    // the client answered our card text "no insurance": the same rule (the no-PLT reply closer's job, not a person's)
    return r.side === 'PLT' && !!r.replies?.some((x) => x.waiting && x.kind === 'no_insurance');
}
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const tried = (r) => (r.calls ? `${plural(r.calls, 'AI call')} so far` : 'no AI call yet');
/** One short sentence: why a person is needed and what the system already tried. Falls back to the row's own text. */
export function whyFor(r, m) {
    const k = r.gap?.key;
    if (k === 'no_own_auto')
        return `${/pedestrian/i.test(m.caseType) ? 'Pedestrians' : 'Cyclists and scooter riders'} are often covered by their household's car policy; the client has not said yet`;
    if (k === 'plt_unknown')
        return 'No card, dec page, intake answer or note names the client\'s insurer';
    if (k === 'plt_texted_before')
        return 'We already texted the client once for the card (one text per case) and got none back';
    if (k === 'plt_minor')
        return 'The client is a minor, so the system does not text them; a parent has the card';
    if (k === 'plt_conflict')
        return 'Our records disagree on whether the client has auto insurance';
    if (k === 'plt_police' || k === 'plt_dec' || k === 'plt_card' || k === 'plt_insurer_known')
        return `${r.text}; the system does not create a record from it on its own`;
    if (k === 'police_names')
        return `The police report names ${r.tcrCarrier ?? 'the at-fault carrier'}, but there is no DEF record to call`;
    if (k === 'police_no_insurer' || k === 'no_report_filed')
        return 'Nothing in our files names the other driver\'s insurer';
    if (k === 'police_not_started')
        return 'The police report request task was never started, so no report is coming';
    if (k?.startsWith('not_requested'))
        return 'No police report request exists, and nothing names the other driver\'s insurer';
    if (k === 'police_client_fault')
        return 'The police report reads our client as at fault; a person decides before anything opens';
    if (k === 'companion')
        return "Second client: the system copies the main client's record only when every rule it checks passes; one does not here (the row says which), so a person adds it in Salesforce";
    if (k === 'companion_own')
        return "Second client: their own files name another insurer, so this may be their own policy; the system never merges a record over that";
    if (k === 'companion_soon')
        return "Second client: the system copies the main client's record and claim at its hourly run, 6 hours after the case and a day after Ins Creation finishes";
    if (k === 'companion_waits')
        return "Second client: nothing to copy until the main client's record has a claim; the system copies it within the case's first 30 days";
    if (r.fix === 'companion')
        return "Second client: the system copies the main client's claim number when nothing on the two records disagrees; here something does, so a person enters it";
    if (r.fix === 'companion_carrier')
        return "Second client: the main client's claim is with another carrier, so there is nothing to copy; the AI never calls for a second client";
    if (hitRunConfirm(r))
        return `${r.hitRun.label}; the client advocate decides no 3P`;
    if (r.status === 'need' && r.replies?.some((x) => x.waiting)) {
        const w = r.replies.find((x) => x.waiting);
        return `${w.label}; the client is waiting on a person`;
    }
    if (!r.gap && r.carrier && notACarrierName(r.carrier))
        return `The record's carrier is "${r.carrier}", which is not an insurer, so the AI never calls it`;
    if (/new info needed to recall/i.test(r.text))
        return 'The carrier said the policy or the carrier was wrong; the system calls again once a new policy number or carrier number is on the record';
    if (/transferred the call to our team/i.test(r.text))
        return 'The AI handed the call to our team and nobody picked up, so the claim may not be open';
    switch (r.fix) {
        case 'nopolicy': return r.side === 'PLT' ? `No policy number on the client's record, so the AI can't call (${tried(r)})`
            : `The insurer is known but not the policy number or insured, so the AI can't call (${tried(r)})`;
        case 'aifail': return `The carrier couldn't find this policy on ${plural(r.calls || 1, 'AI call')}`;
        case 'missing': return `The carrier asked for a fact we don't have (${tried(r)})`;
        case 'live': return `${r.carrier ?? 'This carrier'} only takes this claim from a person`;
        case 'cap': return 'The AI already called 3 times this week';
        case 'nophone': return `No claims phone number for ${r.carrier ?? 'the carrier'}, so the AI can't call`;
        case 'nocarrier': return r.side === 'DEF' ? 'The DEF record has no carrier, so there is nobody to call' : 'The record has no carrier, so there is nobody to call';
        case 'review':
        case 'handoff': return `The last AI call ended without a clear result${r.lastCall ? ` (${r.lastCall.result})` : ''}`;
        case 'elsewhere': return 'The carrier said to file online, by email or at another number';
        default: return r.text || r.need || 'A person needs to look at it';
    }
}
const ms = (s) => Date.parse(s.replace(/\+0000$/, 'Z'));
const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// Kimi's UI assessment (Rod 2026-10-13 "1 a", change 6): a Why line that says again what the row already says ("No policy number or insured
// name, so the AI can't call" / Why: "The insurer is known but not the policy number or insured, so the AI can't call") is dropped. The rule:
// the why's content words (4+ letters, common words left out, the "(2 AI calls so far)" tail left out: the row's own evidence shows calls)
// are at least RESTATE_SHARE inside the row's text and Need line.
const STOP = new Set(['this', 'that', 'with', 'from', 'have', 'been', 'they', 'them', 'their', 'there', 'what', 'when', 'which', 'would', 'about',
    'into', 'only', 'than', 'then', 'also', 'does', 'just', 'some', 'such', 'your', 'here', 'were', 'will', 'still', 'yet', 'not', 'the', 'and']);
export const RESTATE_SHARE = 0.6;
const words = (s) => new Set(norm(s).split(' ').filter((w) => w.length >= 4 && !STOP.has(w)));
/** true when a why adds nothing to what the row already says */
export function restatesRow(why, r) {
    const core = why.replace(/\s*\((?:\d+ AI calls? so far|no AI call yet)\)\s*$/i, '');
    const w = [...words(core)];
    if (!w.length)
        return true;
    const on = words(`${r.text} ${r.need ?? ''}`);
    return w.filter((x) => on.has(x)).length / w.length >= RESTATE_SHARE;
}
// The Queue's grace period (the three-view design, phase 1; Rod 2026-10-13 "1a 2a"): a row reaches a person only after one controller cycle
// (claims-controller-watch runs every 15 minutes), so the system gets its first try ("17 m: No AI call was made on this record" resolved
// itself on the next run). The clock is the row's waitingSince: the later of the record's and the case's creation for a record never called,
// the last call for a failed call, the hold start for a police hold, the reply's time for a client reply (status.ts / board.ts set it).
// Groups the system has nothing to try for wait for nobody: a client's reply, the hit-and-run decision, reading the police report.
export const QUEUE_GRACE_MS = 15 * 60 * 1000;
export const ALWAYS_NOW = new Set(['reply', 'hitrun', 'report']);
export function inGrace(it, now) {
    return !ALWAYS_NOW.has(it.group) && now.getTime() - ms(it.row.waitingSince) < QUEUE_GRACE_MS;
}
/** the Need line of a record whose carrier is not an insurer (the "carrier" group); its Why line names what the record says */
export const CARRIER_NEED = "The real insurer on the record, or the self-insured owner's claims office";
function itemOf(m, r) {
    const group = todoGroup(r);
    const row = group === 'carrier' ? { ...r, need: CARRIER_NEED } : r;
    const w = whyFor(row, m);
    // a why that only repeats the row's own text adds nothing: the row then keeps its "Need:" line
    const why = norm(w) === norm(row.text) || norm(row.text).startsWith(norm(w)) || restatesRow(w, row) ? '' : w;
    return { matter: m, row, group, why, urgent: urgentTag(r, m.priority) };
}
/** Final review 10-29 (I4): a record the Found-a-number group lists with an Approve (core found_number.ts foundOwned) is in that group alone, so
 *  it is in no other Queue group and counted once. Keyed on the record's id, never on a row's words; a record the group does not list keeps its
 *  row here (never in no group). owned: 15-character insurance ids. */
export function ownedByFound(r, owned) { return owned.has(String(r.insuranceId ?? '').slice(0, 15)); }
/** Codex r1 (F8): handled = core found_number.ts foundHandled: a record whose approval the system is still handling loses its "Enter the policy
 *  number" row (the policy group only; its other rows stay); foundQueueTab adds the records whose status rides on their other row (deferred: the
 *  status each of them carries) */
function assemble(allItems, working, now, owned, handled = new Set(), deferred = new Map()) {
    const items = allItems.filter((it) => !ownedByFound(it.row, owned) && !(it.group === 'policy' && ownedByFound(it.row, handled)))
        .map((it) => { const s = deferred.get(String(it.row.insuranceId ?? '').slice(0, 15)); return s ? { ...it, foundStatus: s } : it; });
    const parked = [];
    const fresh = [];
    const live = [];
    for (const it of items)
        (isParked(it.row) ? parked : inGrace(it, now) ? fresh : live).push(it);
    const by = new Map();
    for (const it of live) {
        const xs = by.get(it.group) ?? [];
        xs.push(it);
        by.set(it.group, xs);
    }
    const groups = [...by.entries()].sort((a, b) => TODO_GROUPS[a[0]].order - TODO_GROUPS[b[0]].order).map(([key, xs]) => ({
        key, label: TODO_GROUPS[key].label,
        items: xs.sort((a, b) => (b.urgent?.score ?? 0) - (a.urgent?.score ?? 0) || ms(a.row.waitingSince) - ms(b.row.waitingSince)),
    }));
    return { groups, working, parked, fresh, total: live.length };
}
/** The to-do view of a board window: groups in their order, each urgent first then the longest wait; working, parked and fresh rows apart. */
export function buildTodo(matters, now = new Date(), owned = new Set(), handled = new Set(), deferred = new Map()) {
    const items = [];
    const working = [];
    for (const m of matters) {
        for (const r of m.rows) {
            if (isIssueRow(r))
                items.push(itemOf(m, r));
            else if (r.status === 'working')
                working.push(itemOf(m, r));
        }
    }
    return assemble(items, working, now, owned, handled, deferred);
}
/** The Queue over the issues list (its 14-day and Older tabs): the same groups, order and folds as buildTodo. The issues list carries no
 *  working rows; pass the 14-day board's cases to fold theirs ("the system is handling N"). */
export function buildQueue(issues, now = new Date(), workingFrom = [], owned = new Set(), handled = new Set(), deferred = new Map()) {
    const working = [];
    for (const m of workingFrom)
        for (const r of m.rows)
            if (!isIssueRow(r) && r.status === 'working')
                working.push(itemOf(m, r));
    return assemble(issues.map((i) => itemOf(i.matter, i.row)), working, now, owned, handled, deferred);
}
export function workingSplit(working) {
    const n = { scheduled: 0, system: 0, client: 0, police: 0, carrier: 0 };
    for (const { row: r } of working) {
        if (r.who === 'client')
            n.client++;
        else if (r.who === 'police')
            n.police++;
        else if (r.who === 'carrier')
            n.carrier++;
        else if (r.nextAt)
            n.scheduled++;
        else
            n.system++;
    }
    const L = { scheduled: 'a call scheduled', system: 'calling, texting or filing now', client: 'waiting on the client',
        police: 'waiting on a police report', carrier: 'waiting on the carrier' };
    return Object.keys(n).filter((k) => n[k]).map((k) => ({ key: k, label: L[k], count: n[k] }));
}
//# sourceMappingURL=todo.js.map